import { useState } from "react";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { GlossaryText } from "@/components/Term";
import gSeriesImg from "@/assets/g-series-hero-render.webp";
import dSeriesImg from "@/assets/d-series-hero.webp";
import dSeriesImg640 from "@/assets/d-series-hero-640.webp";
import dSeriesImg960 from "@/assets/d-series-hero-960.webp";
import synthiaImg from "@/assets/synthia/spectrum-editor.png";

const Hero = () => {
  const { t } = useTranslation();

  const slides = [
    {
      key: "g" as const,
      tabLabel: "G-Series™",
      tabSub: t("systems.gSubtitle"),
      label: t("hero.slides.g.label"),
      description: t("hero.slides.g.description"),
      href: "/systems/g-series",
      image: gSeriesImg,
      srcSet: undefined as string | undefined,
      width: 1600,
      height: 900,
      alt: "Photosynthetic G-Series top-light LED fixture mounted on a greenhouse trellis with multi-channel LEDs visible",
    },
    {
      key: "d" as const,
      tabLabel: "D-Series™",
      tabSub: t("systems.dSubtitle"),
      label: t("hero.slides.d.label"),
      description: t("hero.slides.d.description"),
      href: "/systems/d-series",
      image: dSeriesImg,
      srcSet: `${dSeriesImg640} 640w, ${dSeriesImg960} 960w, ${dSeriesImg} 1192w`,
      width: 1192,
      height: 671,
      alt: "Photosynthetic D-Series rack-mounted LED grow light",
    },
    {
      key: "s" as const,
      tabLabel: "Synthia™",
      tabSub: t("nav.items.synthia.description"),
      label: t("hero.slides.s.label"),
      description: t("hero.slides.s.description"),
      href: "/software/synthia",
      image: synthiaImg,
      srcSet: undefined as string | undefined,
      width: 854,
      height: 971,
      alt: "Synthia control software interface showing spectrum editor",
    },
  ];

  const [activeKey, setActiveKey] = useState<"g" | "d" | "s">("g");
  const slide = slides.find((s) => s.key === activeKey) ?? slides[0];

  type Stat = {
    label: string;
    value: string;
    to?: string;
    cite?: string;
    citeHref?: string;
    linkLabel?: string;
  };

  const stats: Stat[] = [
    {
      label: t("hero.stats.energySavings"),
      value: t("hero.stats.energySavingsValue"),
      citeHref: "#measured-impact",
      cite: t("hero.stats.viewImpact"),
    },
    {
      label: t("hero.stats.efficacy"),
      value: t("hero.stats.efficacyValue"),
      citeHref: "#systems",
      cite: "Tech specs",
    },
    {
      label: t("hero.stats.pricePerformance"),
      value: t("hero.stats.pricePerformanceValue"),
      citeHref: "#roi-calculator",
      cite: t("hero.stats.runRoi"),
    },
    {
      label: t("hero.stats.integration"),
      value: t("hero.stats.integrationValue"),
      to: "/resources/downloads",
      linkLabel: t("hero.stats.downloads"),
    },
  ];

  const linkClass =
    "mt-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.15em] text-primary hover:underline underline-offset-4";

  const renderHighlightedTitle = (title: string, highlight: string) => {
    const idx = title.indexOf(highlight);
    if (idx < 0) return title;
    return (
      <>
        {title.slice(0, idx)}
        <span className="text-primary">{highlight}</span>
        {title.slice(idx + highlight.length)}
      </>
    );
  };

  return (
    <section className="relative px-6 pt-32 pb-20 lg:px-8 lg:pt-40 lg:pb-28">
      <div className="mx-auto max-w-7xl">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-12 lg:items-center">
          {/* Left column: copy */}
          <div className="order-1 lg:col-span-7 flex flex-col gap-8">
            <div className="flex items-center gap-3">
              <span className="h-1.5 w-1.5 rounded-full bg-primary" />
              <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                {t("hero.tag")}
              </span>
            </div>

            <h1 className="text-[clamp(2.75rem,5.5vw,4.5rem)] font-normal leading-[1.05] tracking-[-0.03em] text-foreground">
              {renderHighlightedTitle(t("hero.title"), t("hero.titleHighlight"))}
            </h1>

            <p className="max-w-xl text-lg leading-relaxed text-muted-foreground">
              <GlossaryText>{t("hero.subtitle")}</GlossaryText>
            </p>

            <div className="flex flex-col gap-3 pt-2 sm:flex-row sm:gap-4">
              <Link
                to="/resources/light-plan"
                className="inline-flex items-center justify-center gap-3 bg-foreground px-8 py-4 text-sm font-medium tracking-wide text-background transition-colors hover:bg-primary group"
              >
                {t("hero.requestLightPlan", "Create light plan")}
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <a
                href="#systems"
                className="inline-flex items-center justify-center gap-2 border border-border px-8 py-4 text-sm font-medium tracking-wide text-foreground transition-colors hover:bg-muted"
              >
                {t("hero.exploreSystems")}
              </a>
            </div>
          </div>

          {/* Right column: product card with user-controlled tabs */}
          <div className="order-2 lg:col-span-5 relative lg:pt-12">
            {/* Tabs */}
            <div
              role="tablist"
              aria-label="Product selector"
              className="flex gap-8 border-b border-border mb-0"
            >
              {slides.map((s) => {
                const active = s.key === activeKey;
                return (
                  <button
                    key={s.key}
                    role="tab"
                    aria-selected={active}
                    onClick={() => setActiveKey(s.key)}
                    className={`pb-4 -mb-px text-left transition-colors ${
                      active
                        ? "border-b-2 border-primary text-foreground"
                        : "border-b-2 border-transparent text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    <span className="block text-sm font-semibold tracking-[0.1em] uppercase">
                      {s.tabLabel}
                    </span>
                    <span className="block text-[10px] font-normal mt-0.5 text-muted-foreground">
                      {s.tabSub}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Product card */}
            <div className="relative overflow-hidden flex flex-col">
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-primary/5 blur-3xl rounded-full pointer-events-none" />

              <div className="relative h-[320px] overflow-hidden border border-border bg-card sm:h-[360px] lg:h-[380px]">
                <img
                  key={slide.key}
                  src={slide.image}
                  srcSet={slide.srcSet}
                  sizes="(min-width: 1024px) 40vw, 100vw"
                  alt={slide.alt}
                  width={slide.width}
                  height={slide.height}
                  loading="eager"
                  decoding="async"
                  {...({ fetchpriority: "high" } as { fetchpriority: "high" })}
                  className="absolute inset-0 h-full w-full object-cover object-top"
                />
              </div>

              <div className="relative z-10 pt-6 space-y-2 min-h-[160px] flex flex-col">
                <p className="text-base leading-snug text-foreground line-clamp-2">
                  {slide.description}
                </p>
                <Link
                  to={slide.href}
                  className="group self-start inline-flex items-center gap-1.5 pt-2 text-xs font-semibold text-foreground transition-colors hover:text-primary"
                >
                  <span className="relative">
                    {slide.key === "s" ? t("hero.viewPlatform", "View platform") : t("hero.viewSpecs")}
                    <span className="absolute -bottom-0.5 left-0 h-px w-0 bg-primary transition-all duration-300 group-hover:w-full" />
                  </span>
                  <ArrowRight className="h-3 w-3 transition-transform duration-300 group-hover:translate-x-0.5" />
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="mt-24 grid grid-cols-2 gap-10 border-t border-border pt-10 lg:grid-cols-4">
          {stats.map((stat) => (
            <div key={stat.label} className="space-y-2">
              <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-muted-foreground">
                {stat.label}
              </p>
              <p className="text-2xl font-normal tracking-tight text-foreground tabular-nums">
                {stat.value}
              </p>
              {stat.to && stat.linkLabel && (
                <Link to={stat.to} className={linkClass}>
                  {stat.linkLabel} ↗
                </Link>
              )}
              {stat.citeHref && stat.cite && (
                <a href={stat.citeHref} className={linkClass}>
                  {stat.cite} ↗
                </a>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Hero;
